import { h, Host } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { getViewportWidth } from "../../utils/getViewportWidth";
/**
 * @slot (default) - The content of the accordion
 * @slot title - the element for the accordion title
 */
export class Accordion {
    constructor() {
        this.noAccordionFrom = null;
        this.activeTitleClass = "ux-text-link--green";
        this.noClickSelector = "";
        this.isExpanded = false;
        this.noAccordion = this.noAccordionFrom !== null ? window.innerWidth >= this.noAccordionFrom : false;
        this.windowWidth = getViewportWidth();
    }
    baseClass = "ux-accordion";
    contentContainer;
    titleContainer;
    title;
    observer;
    element;
    connectedCallback() {
        window.addEventListener("resize", this.onResize);
    }
    componentDidLoad() {
        // @ts-expect-error Check for typing
        this.title = this.titleContainer.firstElementChild;
        this.element.querySelector("slot[name='title']")?.addEventListener("slotchange", () => {
            // @ts-expect-error Check for typing
            this.title = this.titleContainer.firstElementChild;
        });
        this.setIsExpanded();
        this.observer = new MutationObserver(() => this.handleSlotChange());
        this.observer.observe(this.contentContainer, { childList: true, subtree: true });
    }
    onResize = debounce(() => {
        this.windowWidth = getViewportWidth();
        this.setIsExpanded();
        if (!this.noAccordionFrom) {
            return;
        }
        this.noAccordion = this.windowWidth >= this.noAccordionFrom;
        if (this.noAccordion && this.title !== null) {
            this.title.classList.remove(this.activeTitleClass);
        }
    }, 200);
    /**
     * Checks if the clicked element or its parentElement has any match with the given selector(tag, #id, .class)
     *
     * @param {string} selector - selector we are looking for
     * @param {object} clickedElement - the clicked event target
     * @return {boolean}
     */
    matchingElement = (selector, clickedElement) => {
        return clickedElement.closest(selector);
    };
    // eslint-disable-next-line @stencil-community/no-unused-watch
    setIsExpanded() {
        this.setOrRemoveTitleClass();
        if (this.isExpanded) {
            this.contentContainer.style.maxHeight = this.contentContainer.scrollHeight + (this.windowWidth > 767 ? 65 : 50) + "px";
        }
        else {
            // @ts-expect-error Check for typing
            this.contentContainer.style.maxHeight = null;
        }
    }
    handleCollapsibleExpansion() {
        this.onResize();
    }
    handleSlotChange = () => {
        this.onResize();
    };
    setOrRemoveTitleClass() {
        if (this.activeTitleClass !== "" && this.title !== null) {
            if (this.isExpanded) {
                this.title.classList.add(this.activeTitleClass);
            }
            else {
                this.title.classList.remove(this.activeTitleClass);
            }
        }
    }
    handleAccordionClick = (e) => {
        const clickTarget = e.target;
        if (this.noClickSelector !== "") {
            if (this.matchingElement(this.noClickSelector, clickTarget)) {
                return;
            }
        }
        if (this.noAccordion) {
            return;
        }
        this.isExpanded = !this.isExpanded;
    };
    disconnectedCallback() {
        this.observer.disconnect();
        window.removeEventListener("resize", this.onResize);
    }
    render() {
        return (h(Host, { key: 'bd6ad452dcb27d4fec2a45818103e3e05079d895', class: {
                [this.baseClass]: true,
                [`${this.baseClass}--active`]: this.isExpanded && !this.noAccordion
            } }, h("button", { key: '4b6b12853aa5aa04ac825f6c00b48b5b00e629c9', class: {
                [`${this.baseClass}__button`]: true,
                [`${this.baseClass}__title-container`]: !this.noAccordion
            }, onClick: (e) => this.handleAccordionClick(e),
            // @ts-expect-error Check for typing
            ref: (el) => (this.titleContainer = el), "aria-expanded": this.isExpanded, type: "button" }, h("slot", { key: 'ab23a966c4ac2ea78665ca3a1e76acdd9592bd02', name: "title" }), !this.noAccordion && h("ux-icon", { key: 'b3ab6db05f4949dee279bb089effe8acfcdeb269', name: "chevron-down", weight: "bold" })), h("div", { key: 'bbd0e71e839e7b146471a1f4412263d6d0e37968',
            // @ts-expect-error Check for typing
            ref: (el) => (this.contentContainer = el), class: {
                [`${this.baseClass}__content-container`]: !this.noAccordion
            }, "aria-hidden": !this.isExpanded && !this.noAccordion }, h("slot", { key: '5b00234b49afbee7172d8a47ce55d5aa21cbdd26' }))));
    }
    static get is() { return "ux-accordion"; }
    static get originalStyleUrls() {
        return {
            "$": ["accordion.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["accordion.css"]
        };
    }
    static get properties() {
        return {
            "noAccordionFrom": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number | null",
                    "resolved": "null | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The amount in pixels from which the accordion functionality is deactivated"
                },
                "attribute": "no-accordion-from",
                "reflect": false,
                "defaultValue": "null"
            },
            "activeTitleClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The class to be passes to the title of the accordion when it becomes active"
                },
                "attribute": "active-title-class",
                "reflect": false,
                "defaultValue": "\"ux-text-link--green\""
            },
            "noClickSelector": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If a click event is detected on this selector, the accordion will not trigger"
                },
                "attribute": "no-click-selector",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "isExpanded": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determines if accordion is opened on initial view"
                },
                "attribute": "is-expanded",
                "reflect": true,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "noAccordion": {},
            "windowWidth": {}
        };
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "isExpanded",
                "methodName": "setIsExpanded"
            }];
    }
    static get listeners() {
        return [{
                "name": "uxCollapsibleExpanded",
                "method": "handleCollapsibleExpansion",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=accordion.js.map
